import classNames from "../../utils/classNames";
import stringToColour from "../../utils/stringToColour";
import Tooltip from "../../components/shared/Tooltip";
import { fullNameFor, initialsFor } from "../../utils/chatHelpers";
import { IChatPresence, IChatUser } from "../../models/Chat";

interface ChatAvatarProps {
  user: Pick<IChatUser, "id" | "forename" | "surname" | "presence" | "role">;
  size?: "xs" | "sm" | "md" | "lg";
  showPresence?: boolean;
  tooltip?: boolean;
  onClick?: () => void;
  className?: string;
}

const sizeClasses: Record<NonNullable<ChatAvatarProps["size"]>, string> = {
  xs: "h-6 w-6 text-[11px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-lg",
};

const dotClasses: Record<NonNullable<ChatAvatarProps["size"]>, string> = {
  xs: "h-1.5 w-1.5 ring-1",
  sm: "h-2 w-2 ring-2",
  md: "h-2.5 w-2.5 ring-2",
  lg: "h-3 w-3 ring-2",
};

const presenceColour: Record<IChatPresence, string> = {
  online: "bg-emerald-500",
  away: "bg-amber-400",
  offline: "bg-gray-400",
};

export default function ChatAvatar({
  user,
  size = "md",
  showPresence = false,
  tooltip = true,
  onClick,
  className,
}: ChatAvatarProps) {
  const body = (
    <span
      onClick={onClick}
      className={classNames(
        "relative inline-flex items-center justify-center rounded-full font-semibold text-white select-none",
        sizeClasses[size],
        onClick ? "cursor-pointer" : "",
        className
      )}
      style={{ background: stringToColour(user.id || "anon") }}
    >
      <span className="leading-none">{initialsFor(user)}</span>

      {showPresence && user.presence ? (
        <span
          className={classNames(
            "absolute -bottom-0.5 -right-0.5 rounded-full ring-white",
            dotClasses[size],
            presenceColour[user.presence]
          )}
        />
      ) : null}
    </span>
  );

  if (!tooltip) return body;

  const label = user.role
    ? `${fullNameFor(user)} — ${user.role}`
    : fullNameFor(user);

  return <Tooltip content={label}>{body}</Tooltip>;
}
